import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { fetchUser } from '../services/users';
import usePagination from './usePagination';
import { fetchRecord, getProcessedRecord, getRecord, getRecords } from 'services/records';
import { calculateHeartRateZones } from 'utils/hr';

const id = 'records';

export const useRecords = (patient, group, plan, type = null, noPagination = false) => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const { isPending, isError, data } = useQuery({
        queryKey: [
            'records',
            { paginationModel, patient, group, plan, type, noPagination }
        ],
        queryFn: async () => {
            if (noPagination) {
                let allData = [];
                let pageToken = null;
                let hasNext = true;

                while (hasNext) {
                    const resp = await getRecords({
                        pageLimit: 50,
                        pageToken,
                        patient,
                        group,
                        plan,
                        type
                    });
                    allData = [...allData, ...(resp?.data || [])];
                    const nextToken = resp?.headers?.['next-page-token'];
                    if (nextToken) {
                        pageToken = nextToken;
                    } else {
                        hasNext = false;
                    }
                }
                return { data: allData };
            } else {
                return getRecords({
                    pageLimit: paginationModel.pageSize,
                    pageToken: currentToken(paginationModel.page),
                    patient,
                    group,
                    plan,
                    type
                });
            }
        }
    });

    useEffect(() => {
        if (!noPagination && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data, noPagination]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

export const useRecordsTypes = (patient, type) => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const { isPending, isError, data } = useQuery({
        queryKey: [id, { paginationModel, patient, type }],
        queryFn: () =>
            getRecords({
                pageLimit: paginationModel.pageSize,
                pageToken: currentToken(paginationModel.page),
                patient,
                type
            })
    });

    useEffect(() => {
        if (data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

export const useRecord = (uuid, dtype, enabled = true) => {
    const { isPending, isError, data } = useQuery({
        queryKey: [uuid, dtype],
        queryFn: () => getRecord({ uuid, dtype }),
        enabled: enabled && Boolean(uuid)
    });

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError
    };
};

export const useRecordHr = (record, user_data) => {
    const canFetch = Boolean(record?.uuid && user_data);

    const { data, isLoading, error } = useRecord(
        canFetch ? record.uuid : null,
        'HR',
        canFetch
    );

    const dataPoints = useMemo(() => {
        if (!data?.ts?.length) return [];

        return data.ts.map((timestamp, index) => ({
            time: new Date(timestamp).toLocaleTimeString('pt-PT', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }),
            hr: data.value[index]
        }));
    }, [data]);

    const dataHrZone = useMemo(() => {
        if (!data?.value?.length || !user_data || !record) return null;

        return calculateHeartRateZones(data.value, user_data, record.ti, record.tf);
    }, [data, user_data, record]);

    const hrValues = data?.value ?? [];

    return {
        hrMax: hrValues.length ? Math.max(...hrValues) : null,
        hrMin: hrValues.length ? Math.min(...hrValues) : null,
        dataHrZone,
        hrPoints: dataPoints,
        isLoading: canFetch ? isLoading : true,
        error
    };
};

/**
 * Custom hook to fetch GPS data for a specific record.
 *
 * @param {string} uuid - The unique identifier of the record.
 */
export const useRecordGPS = (uuid) => {
    const { data, isLoading, error } = useRecord(uuid, 'GPS');

    const gpsPoints = useMemo(
        () => data.lat?.map((lat, index) => [lat, data.lon[index]]) || [],
        [data]
    );

    return {
        gpsPoints,
        data,
        isLoading,
        error
    };
};

/**
 * Custom hook to fetch ANN data for a specific record.
 *
 * @param {string} uuid - The unique identifier of the record.
 */
export const useRecordANN = (uuid) => {
    const shouldFetch = Boolean(uuid);

    const { data, isLoading, error } = useRecord(
        shouldFetch ? uuid : null,
        'ANN',
        shouldFetch
    );

    return {
        data: shouldFetch ? data : null,
        isLoading: shouldFetch ? isLoading : false,
        error: shouldFetch ? error : null
    };
};

/**
 * Fetch processed ECG preview blocks (downsampled), paginated server-side.
 */
export const useRecordECGPreview = (uuid, pageSize = 5) => {
    const shouldFetch = Boolean(uuid);

    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination(0, pageSize);

    const { isPending, isError, data } = useQuery({
        queryKey: ['record-ecg-preview-processed', { uuid, paginationModel }],
        enabled: shouldFetch,
        queryFn: () =>
            getProcessedRecord({
                uuid,
                dtype: 'ECG-Preview',
                pageLimit: paginationModel.pageSize,
                pageToken: currentToken(paginationModel.page)
            })
    });

    useEffect(() => {
        if (shouldFetch && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [shouldFetch, data, paginationModel.page, resolveHeader]);

    return {
        data: shouldFetch ? data?.data || [] : [],
        raw: shouldFetch ? data : null,
        isLoading: shouldFetch ? isPending : false,
        error: shouldFetch ? isError : null,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

/**
 * Fetch processed full-resolution ECG blocks, paginated server-side.
 */
export const useRecordECGProcessed = (uuid, pageSize = 5) => {
    const shouldFetch = Boolean(uuid);

    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination(0, pageSize);

    const { isPending, isError, data } = useQuery({
        queryKey: ['record-ecg-processed', { uuid, paginationModel }],
        enabled: shouldFetch,
        queryFn: () =>
            getProcessedRecord({
                uuid,
                dtype: 'ECG',
                pageLimit: paginationModel.pageSize,
                pageToken: currentToken(paginationModel.page)
            })
    });

    useEffect(() => {
        if (shouldFetch && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [shouldFetch, data, paginationModel.page, resolveHeader]);

    return {
        data: shouldFetch ? data?.data || [] : [],
        raw: shouldFetch ? data : null,
        isLoading: shouldFetch ? isPending : false,
        error: shouldFetch ? isError : null,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};
